/**
 * Нетарифка (Л1–Л3): схемы запросов API и формы ответов — общие для сервера и сайта.
 * Константы и подписи — constants/customs.ts, разбор ответа брокера — utils/customs.ts.
 */
import { z } from 'zod'
import {
  BrokerRequestItemStatus,
  BrokerRequestStatus,
  ChestnyZnak,
  CustomsCheckStatus,
  CustomsSource,
} from '../constants/customs'
import { SAFE_STRING } from './request.types'
import { DATASHEET_URL_MAX, DATASHEET_URL_RE } from '../utils/customs'
import type { CustomsBrokerRef } from './customs-brokers.types'

const uuid = z.string().uuid()
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД')
const note = (max: number) => SAFE_STRING(max).nullable().optional()

/** Ссылка на даташит: только http(s), до 1000 символов. */
export const DatasheetUrlSchema = z
  .string()
  .trim()
  .max(DATASHEET_URL_MAX)
  .regex(DATASHEET_URL_RE, 'Ссылка на даташит должна начинаться с http:// или https://')

// ─── Очередь ────────────────────────────────────────────────────────────────

export const CUSTOMS_QUEUE_TABS = ['active', 'done', 'all'] as const
export type CustomsQueueTab = (typeof CUSTOMS_QUEUE_TABS)[number]

export const CustomsQueueQuerySchema = z.object({
  tab: z.enum(CUSTOMS_QUEUE_TABS).default('active'),
  status: z.nativeEnum(CustomsCheckStatus).optional(),
  search: z.string().trim().max(200).optional(),
  managerId: uuid.optional(),
  matrix: z.enum(['Китай', 'Производители', 'Стоки']).optional(),
  /** '1' — только с красным флагом. */
  attention: z.enum(['0', '1']).optional(),
  chestnyZnak: z.nativeEnum(ChestnyZnak).optional(),
  /** '1' (по умолчанию) — без позиций архивных заявок. */
  hideArchived: z.enum(['0', '1']).default('1'),
  /** Заявки, созданные с этой даты (включительно). */
  requestFrom: isoDate.optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(200),
})
export type CustomsQueueQuery = z.input<typeof CustomsQueueQuerySchema>

// ─── Ответ по позиции ───────────────────────────────────────────────────────

export const CustomsAnswerInputSchema = z.object({
  tnvedCode: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Код ТН ВЭД — 10 цифр')
    .nullable()
    .optional(),
  /** Доля: 0.08 = 8 %. */
  dutyRate: z.number().finite().min(0).max(1).nullable().optional(),
  customsFeeRaised: z.boolean().nullable().optional(),
  customsFeeAmount: z.number().finite().min(0).max(10_000_000).nullable().optional(),
  nonTariffNote: note(5000),
  certificateNote: note(5000),
  featuresNote: note(5000),
  chestnyZnak: z.nativeEnum(ChestnyZnak),
  chestnyZnakFrom: isoDate.nullable().optional(),
})
export type CustomsAnswerInput = z.infer<typeof CustomsAnswerInputSchema>

/** Ручной ввод ответа брокера — сразу на несколько позиций (один партномер из разных заявок). */
export const CustomsAnswerSchema = z.object({
  itemIds: z.array(uuid).min(1).max(500),
  answer: CustomsAnswerInputSchema,
})
export type CustomsAnswerDto = z.infer<typeof CustomsAnswerSchema>

/**
 * Правка ячейки ответа прямо в очереди (логист): только изменённые поля, остальное
 * сервер берёт из ответа позиции. Позиция без ответа становится «Ответ получен» —
 * как после «Ввести ответ».
 */
export const CustomsAnswerPatchSchema = CustomsAnswerInputSchema.partial().refine(
  (v) => Object.values(v).some((x) => x !== undefined),
  { message: 'Нечего сохранять: не передано ни одного поля ответа' },
)
export type CustomsAnswerPatchDto = z.infer<typeof CustomsAnswerPatchSchema>

export const CUSTOMS_MANUAL_STATUS_VALUES = ['PENDING', 'NEEDS_INFO', 'NOT_REQUIRED'] as const

export const CustomsStatusSchema = z.object({
  itemIds: z.array(uuid).min(1).max(2000),
  status: z.enum(CUSTOMS_MANUAL_STATUS_VALUES),
  /** Для «Нужна информация» — что нужно от менеджера; уходит ему в уведомлении. */
  note: SAFE_STRING(1000).trim().optional(),
})
export type CustomsStatusDto = z.infer<typeof CustomsStatusSchema>

/**
 * «Оставить прошлый ответ» (с 17.10.2026): логист подтверждает ответ из памяти
 * по партномеру — позиция без нового запроса брокеру становится «Ответ получен».
 */
export const CustomsAcceptPreviousSchema = z.object({
  itemIds: z.array(uuid).min(1).max(2000),
})
export type CustomsAcceptPreviousDto = z.infer<typeof CustomsAcceptPreviousSchema>

export const CustomsDatasheetSchema = z.object({
  datasheetUrl: DatasheetUrlSchema.nullable(),
})
export type CustomsDatasheetDto = z.infer<typeof CustomsDatasheetSchema>

// ─── Запросы брокеру ────────────────────────────────────────────────────────

export const CreateBrokerRequestSchema = z.object({
  itemIds: z.array(uuid).min(1).max(2000),
  note: SAFE_STRING(1000).trim().optional(),
  /** Карточка брокера, которому уходит файл; не указан — null/нет поля. */
  brokerId: uuid.nullable().optional(),
})
export type CreateBrokerRequestDto = z.infer<typeof CreateBrokerRequestSchema>

/** Строка ответа брокера — сайт уже разобрал Excel по заголовкам (шаблон на 9 колонок). */
export const BrokerAnswerRowSchema = z.object({
  partNumber: SAFE_STRING(200).trim().min(1),
  brand: note(200),
  description: note(2000),
  /** Как в файле; сервер оставит только 10 цифр. */
  tnvedCode: note(40),
  nonTariffNote: note(5000),
  certificateNote: note(5000),
  featuresNote: note(5000),
  /** Сбор, ₽ (0 или 73 860). */
  customsFee: z.number().finite().min(0).max(10_000_000).nullable().optional(),
  /** Пошлина долей: 0.08 = 8 %. */
  dutyRate: z.number().finite().min(0).max(1).nullable().optional(),
})
export type BrokerAnswerRow = z.infer<typeof BrokerAnswerRowSchema>

export const UploadBrokerAnswerSchema = z.object({
  /** Код запроса из файла (если брокер ответил в нашем же файле) или выбранный запрос. */
  fileCode: z.string().trim().max(80).optional(),
  brokerRequestId: uuid.optional(),
  rows: z.array(BrokerAnswerRowSchema).min(1).max(2000),
})
export type UploadBrokerAnswerDto = z.infer<typeof UploadBrokerAnswerSchema>

/**
 * Записать строку ответа брокера на выбранную логистом позицию (с 30.10.2026):
 * брокер ответил по старому партномеру или записал артикул так, что сайт не узнал.
 */
export const BindBrokerAnswerSchema = z.object({
  itemId: uuid,
  brokerRequestId: uuid,
  row: BrokerAnswerRowSchema,
})
export type BindBrokerAnswerDto = z.infer<typeof BindBrokerAnswerSchema>

export const BrokerRequestsQuerySchema = z.object({
  status: z.nativeEnum(BrokerRequestStatus).optional(),
  /** id брокера или 'none' — запросы без брокера. */
  brokerId: z.union([uuid, z.literal('none')]).optional(),
  search: z.string().trim().max(200).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
})
export type BrokerRequestsQuery = z.input<typeof BrokerRequestsQuerySchema>

// ─── Ответы API ─────────────────────────────────────────────────────────────

export interface CustomsUserRef {
  id: string
  name: string | null
}

/** Проверка позиции — то, что записано по ней (ответ брокера или ручной ввод логиста). */
export interface CustomsCheckDto {
  status: CustomsCheckStatus
  source: CustomsSource | null
  tnvedCode: string | null
  dutyRate: number | null
  customsFeeRaised: boolean | null
  customsFeeAmount: number | null
  nonTariffNote: string | null
  certificateNote: string | null
  featuresNote: string | null
  /** «Описание» из ответа брокера — справочно. */
  brokerDescription: string | null
  chestnyZnak: ChestnyZnak
  chestnyZnakFrom: string | null
  attention: boolean
  attentionReasons: string[]
  documentsRequired: boolean
  /** Для «Нужна информация» — что просил логист. */
  statusNote: string | null
  checkedAt: string | null
  checkedBy: CustomsUserRef | null
  brokerRequest: { id: string; fileCode: string } | null
  /** Когда и кто из продаж отправил позицию логисту («Отправить брокеру»); null — не отправляли. */
  sentToLogistAt: string | null
  sentToLogistBy: CustomsUserRef | null
  updatedAt: string
}

/**
 * Память по партномеру (Л3): что известно из прошлых проверок. ТН ВЭД и «Честный
 * знак» — доверенные; пошлина, сбор и документы — только справочно «на дату»,
 * по позиции их всегда перезапрашивают у брокера.
 */
export interface CustomsCatalogHintDto {
  partNumber: string
  tnvedCode: string | null
  chestnyZnak: ChestnyZnak
  chestnyZnakFrom: string | null
  lastDutyRate: number | null
  lastFeeRaised: boolean | null
  /** Сумма сбора из последней проверки (₽), если брокер её указал. */
  lastFeeAmount?: number | null
  lastNonTariffNote: string | null
  lastAttention: boolean
  lastCheckedAt: string
  checkCount: number
}

/** Нетарифка позиции в карточке заявки (менеджер, закупка, руководители). */
export interface ItemCustomsSummaryDto {
  /** Позиция везётся из-за рубежа — её можно отправить логисту. */
  eligible: boolean
  /**
   * Показывать нетарифку позиции: матрица из-за рубежа, статус заявки не важен
   * (с 30.10.2026 — и в заявках «Отказ», решение Сергея). Отправить логисту
   * можно только при eligible.
   */
  shown?: boolean
  /** Позиция отправлена логисту и стоит в его очереди. */
  sentToLogist: boolean
  /** Статус нетарифки; null — позицию логисту не отправляли («—»). */
  status: CustomsCheckStatus | null
  check: CustomsCheckDto | null
  hint: CustomsCatalogHintDto | null
}

export interface CustomsQueueRow {
  itemId: string
  requestId: string
  requestNumber: string | null
  requestStatus: string
  requestCreatedAt: string
  clientCode: string | null
  managerId: string
  managerName: string | null
  partNumber: string
  brand: string | null
  category: string | null
  quantity: number
  unit: string | null
  matrix: string | null
  datasheetUrl: string | null
  commentsCount: number
  status: CustomsCheckStatus
  check: CustomsCheckDto | null
  hint: CustomsCatalogHintDto | null
  /** Менеджер изменил партномер после отправки логисту (с 30.10.2026); нет — null. */
  partChange?: CustomsPartChangeDto | null
}

/**
 * Пометка смены партномера: BEFORE_BROKER — изменён после отправки логисту, брокеру
 * ещё не уходил (или ответ вписан вручную); AFTER_BROKER — брокеру ушёл прежний
 * партномер, его ответ может относиться к другой детали.
 */
export interface CustomsPartChangeDto {
  stage: 'BEFORE_BROKER' | 'AFTER_BROKER'
  from: string
}

export interface CustomsQueueCounts {
  active: number
  done: number
  all: number
}

export interface CustomsQueueResponse {
  items: CustomsQueueRow[]
  /** Всего по вкладке и фильтрам — только на первой странице (offset 0), дальше null. */
  total: number | null
  nextOffset: number | null
  /** Счётчики вкладок — только на первой странице (offset 0), дальше null: не считаем заново при догрузке. */
  counts: CustomsQueueCounts | null
}

export interface CustomsQueueOptions {
  managers: CustomsUserRef[]
}

export interface CustomsBulkResult {
  updated: number
  /** Позиции, которых нет в очереди нетарифки (удалены, сменили матрицу, заявка в отказе). */
  skipped: number
}

export interface BrokerAnswerSnapshot {
  description: string | null
  tnvedCode: string | null
  nonTariffNote: string | null
  certificateNote: string | null
  featuresNote: string | null
  customsFee: number | null
  dutyRate: number | null
}

export interface BrokerRequestItemDto {
  id: string
  artKey: string
  partNumber: string
  brand: string | null
  description: string | null
  datasheetUrl: string | null
  tnvedCode: string | null
  positionsCount: number
  status: BrokerRequestItemStatus
  answeredAt: string | null
  answer: BrokerAnswerSnapshot | null
}

export interface BrokerRequestSummaryDto {
  id: string
  fileCode: string
  status: BrokerRequestStatus
  note: string | null
  createdAt: string
  answeredAt: string | null
  logist: CustomsUserRef | null
  /** Кому ушёл файл; у запросов до справочника брокеров (27.09.2026) — null. */
  broker: CustomsBrokerRef | null
  itemsCount: number
  answeredCount: number
}

export interface BrokerRequestPositionDto {
  itemId: string
  requestId: string
  requestNumber: string | null
  managerName: string | null
  partNumber: string
  artKey: string | null
  quantity: number
  status: CustomsCheckStatus
}

export interface BrokerRequestDetailDto extends BrokerRequestSummaryDto {
  items: BrokerRequestItemDto[]
  positions: BrokerRequestPositionDto[]
}

export interface BrokerRequestsResponse {
  items: BrokerRequestSummaryDto[]
  total: number
  nextOffset: number | null
  counts: Record<BrokerRequestStatus, number>
}

export interface BrokerUploadResultDto {
  fileCodes: string[]
  /** Строк файла, сопоставленных с запросом. */
  matchedRows: number
  /** Позиций заявок, получивших ответ. */
  positionsUpdated: number
  /** Позиций с красным флагом после ответа. */
  attentionPositions: number
  /** Партномера из файла, которых нет в открытых запросах брокеру. */
  unmatched: string[]
  /**
   * Строки файла без ответа брокера (заполнена только наша левая часть — артикул,
   * производитель, описание, ТН ВЭД): не записываются, позиции остаются «Отправлено брокеру».
   */
  emptyRows: number
  /**
   * Позиций закрытых запросов, где файл заполнил пустые поля (общая кнопка
   * загрузки, старый файл брокера; с 29.10.2026). Записанное не перезаписывается.
   */
  filledPositions?: number
  /**
   * Брокер записал артикул иначе, чем мы отправили, но строка нашлась однозначно
   * (без хвостов «(код)», «-СНЯТ С ПРОИЗВОДСТВА», альтернатив через «/», по словам) —
   * ответ лёг на наш партномер (с 30.10.2026).
   */
  aliased?: BrokerUploadAliasDto[]
  /** Позиций, у которых ответ из файла уже был записан полностью — дописывать нечего. */
  alreadyComplete?: number
  /**
   * Позиции, на которые ответ не лёг: партномер поправили после отправки брокеру
   * (ответ по старому партномеру) или позиция ушла из очереди (матрица, отмена).
   */
  notApplied?: BrokerUploadNotAppliedDto[]
  /**
   * Позиции запросов этого файла, на которые ответа так и нет, — для выпадающего
   * списка в итоге загрузки: логист выбирает, на какую записать строку брокера.
   */
  openPositions?: BrokerOpenPositionDto[]
}

export interface BrokerOpenPositionDto {
  itemId: string
  brokerRequestId: string
  fileCode: string
  requestNumber: string | null
  /** Партномер позиции сейчас. */
  partNumber: string
  brand: string | null
  /** Партномер, ушедший брокеру, — если менеджер его потом поправил. */
  sentPartNumber: string | null
}

export interface BindBrokerAnswerResultDto {
  itemId: string
  partNumber: string
  attention: boolean
}

export interface BrokerUploadAliasDto {
  /** Как артикул записан в файле брокера. */
  filePart: string
  /** Наш партномер в запросе брокеру. */
  requestPart: string
  fileCode: string | null
}

export interface BrokerUploadNotAppliedDto {
  itemId: string
  requestNumber: string | null
  /** Партномер позиции сейчас. */
  partNumber: string
  /** Партномер, ушедший брокеру. */
  sentPartNumber: string
  reason: 'PART_CHANGED' | 'OUT_OF_QUEUE'
  /** Запрос брокеру, по строке которого пришёл ответ. */
  brokerRequestId?: string
}

/** Строка запроса брокеру для ручной привязки ненайденного артикула. */
export interface BrokerLineOptionDto {
  brokerRequestId: string
  fileCode: string
  partNumber: string
  brand: string | null
  status: BrokerRequestItemStatus
  createdAt: string
}
